import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type BenchmarkDto, benchmarksApi } from "../../lib/benchmarks";

export function BenchmarksTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ["benchmarks"], queryFn: benchmarksApi.list });
  const settings = useQuery({
    queryKey: ["benchmark-settings"],
    queryFn: benchmarksApi.getSettings,
  });

  const [creating, setCreating] = useState(false);
  const [pollingIds, setPollingIds] = useState<Set<string>>(new Set());
  const [workflowBanner, setWorkflowBanner] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: benchmarksApi.create,
    onSuccess: (bm) => {
      queryClient.invalidateQueries({ queryKey: ["benchmarks"] });
      setCreating(false);
      setPollingIds((prev) => new Set(prev).add(bm.id));
      // Auf Hetzner triggert das Backend automatisch sync-benchmarks.yml.
      // Workflow laeuft ~3 Min — Banner einblenden damit User weiss was los ist.
      setWorkflowBanner(
        "Benchmark-Backfill laeuft auf GitHub-Actions (Yahoo blockt unsere Server-IP). " +
          "Dauert ~3 Minuten — die Tabelle aktualisiert sich automatisch.",
      );
    },
  });

  const patchMut = useMutation({
    mutationFn: ({
      id,
      input,
    }: { id: string; input: Parameters<typeof benchmarksApi.update>[1] }) =>
      benchmarksApi.update(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["benchmarks"] }),
  });

  const deleteMut = useMutation({
    mutationFn: benchmarksApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["benchmarks"] }),
  });

  const updateSettingsMut = useMutation({
    mutationFn: benchmarksApi.updateSettings,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["benchmark-settings"] }),
  });

  const backfillMut = useMutation({
    mutationFn: (id: string) => benchmarksApi.backfill(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["benchmarks"] });
      if (
        res &&
        typeof res === "object" &&
        "mode" in res &&
        (res as { mode?: string }).mode === "workflow"
      ) {
        setWorkflowBanner(
          ((res as { message?: string }).message ??
            "Benchmark-Backfill laeuft auf GitHub-Actions (~3 Min).") +
            " Die Tabelle aktualisiert sich automatisch.",
        );
      }
    },
  });

  // Polling fuer Initial-Backfill (10s Interval, 5 Min Timeout —
  // GitHub-Actions-Workflow braucht ~3 Min)
  useEffect(() => {
    if (pollingIds.size === 0) return;
    const startTime = Date.now();
    const interval = setInterval(async () => {
      if (Date.now() - startTime > 5 * 60_000) {
        setPollingIds(new Set());
        clearInterval(interval);
        return;
      }
      for (const id of pollingIds) {
        try {
          const bm = await benchmarksApi.get(id);
          if (bm.initialBackfillCompleted || bm.initialBackfillFailedReason) {
            setPollingIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
            queryClient.invalidateQueries({ queryKey: ["benchmarks"] });
            if (bm.initialBackfillCompleted) setWorkflowBanner(null);
          }
        } catch {
          /* keep polling */
        }
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [pollingIds, queryClient]);

  const activeCount = (list.data ?? []).filter((b) => b.isActiveForKpi).length;
  const anyFailed = (list.data ?? []).some(
    (b) => Boolean(b.initialBackfillFailedReason) && !b.initialBackfillCompleted,
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold">{t("settings.benchmarks.title")}</h2>
        <p className="text-sm mt-0.5" style={{ color: "var(--muted)" }}>
          {t("settings.benchmarks.activeCount", { count: activeCount, max: 3 })}
        </p>
      </div>

      {workflowBanner && (
        <div
          className="fv-card p-4 text-sm flex items-start justify-between gap-3"
          style={{ background: "var(--accent-soft)", borderColor: "var(--accent)" }}
        >
          <div>
            <div className="font-semibold mb-1" style={{ color: "var(--accent)" }}>
              GitHub-Actions-Workflow gestartet
            </div>
            <div style={{ color: "var(--muted)" }}>{workflowBanner}</div>
          </div>
          <button
            type="button"
            onClick={() => setWorkflowBanner(null)}
            className="text-xs"
            style={{ color: "var(--muted)" }}
          >
            ✕
          </button>
        </div>
      )}

      {anyFailed && !workflowBanner && (
        <div
          className="fv-card p-4 text-sm"
          style={{ background: "var(--warn-soft)", borderColor: "var(--warn)" }}
        >
          <div className="font-semibold mb-1" style={{ color: "var(--warn)" }}>
            ⚠ Benchmark-Backfill scheitert
          </div>
          <div style={{ color: "var(--muted)" }}>
            Yahoo Finance blockt den Hetzner-Server. Klick auf <b>„Backfill"</b> in
            einer Zeile, dann uebernimmt der GitHub-Actions-Workflow den Sync.
            Dauert ~3 Minuten.
          </div>
        </div>
      )}

      <div className="fv-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr
              className="text-left text-xs"
              style={{ background: "var(--border-soft)", color: "var(--muted)" }}
            >
              <th className="px-3 py-2 font-semibold w-16">
                {t("settings.benchmarks.col.active")}
              </th>
              <th className="px-3 py-2 font-semibold">{t("settings.benchmarks.col.name")}</th>
              <th className="px-3 py-2 font-semibold">{t("settings.benchmarks.col.symbol")}</th>
              <th className="px-3 py-2 font-semibold">{t("settings.benchmarks.col.currency")}</th>
              <th className="px-3 py-2 font-semibold">{t("settings.benchmarks.col.status")}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((bm) => (
              <BenchmarkRow
                key={bm.id}
                bm={bm}
                polling={pollingIds.has(bm.id)}
                backfillRunning={backfillMut.isPending && backfillMut.variables === bm.id}
                onToggleActive={(active) =>
                  patchMut.mutate({ id: bm.id, input: { isActiveForKpi: active } })
                }
                onDelete={() => {
                  if (
                    confirm(t("settings.benchmarks.confirmDelete", { name: bm.displayName }))
                  ) {
                    deleteMut.mutate(bm.id);
                  }
                }}
                onBackfill={() => {
                  setPollingIds((prev) => new Set(prev).add(bm.id));
                  backfillMut.mutate(bm.id);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        className="px-3 py-2 rounded-lg text-white text-sm font-medium"
        style={{ background: "var(--accent)" }}
        onClick={() => setCreating(true)}
      >
        + {t("settings.benchmarks.addBtn")}
      </button>

      {creating && (
        <CreateBenchmarkModal
          onCancel={() => setCreating(false)}
          onCreate={(input) => createMut.mutate(input)}
          submitting={createMut.isPending}
        />
      )}

      <div className="border-t pt-4" style={{ borderColor: "var(--border)" }}>
        <h3 className="text-base font-semibold mb-2">{t("settings.benchmarks.refreshHour")}</h3>
        <label className="flex items-center gap-2 text-sm">
          <span style={{ color: "var(--muted)" }}>
            {t("settings.benchmarks.dailyRefreshLabel")}
          </span>
          <select
            value={settings.data?.dailyRefreshHourUtc ?? 22}
            onChange={(e) =>
              updateSettingsMut.mutate({ dailyRefreshHourUtc: Number(e.target.value) })
            }
            className="text-sm px-2 py-1.5 rounded-lg"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          >
            {Array.from({ length: 24 }, (_, i) => i).map((h) => (
              <option key={h} value={h}>
                {h.toString().padStart(2, "0")}:00 UTC
              </option>
            ))}
          </select>
        </label>
      </div>

      {createMut.error && (
        <div
          className="text-sm px-3 py-2 rounded-lg"
          style={{ background: "var(--neg-soft)", color: "var(--neg)" }}
        >
          {(createMut.error as Error).message}
        </div>
      )}
      {patchMut.error && (
        <div
          className="text-sm px-3 py-2 rounded-lg"
          style={{ background: "var(--neg-soft)", color: "var(--neg)" }}
        >
          {(() => {
            const err = patchMut.error as Error & { code?: string };
            if (err.code === "BENCHMARK_LIMIT_EXCEEDED") {
              return t("settings.benchmarks.errors.limitExceeded");
            }
            return err.message;
          })()}
        </div>
      )}
    </div>
  );
}

interface BenchmarkRowProps {
  bm: BenchmarkDto;
  polling: boolean;
  backfillRunning: boolean;
  onToggleActive: (active: boolean) => void;
  onDelete: () => void;
  onBackfill: () => void;
}

function BenchmarkRow({
  bm,
  polling,
  backfillRunning,
  onToggleActive,
  onDelete,
  onBackfill,
}: BenchmarkRowProps) {
  const { t } = useTranslation();

  // Status-Logik korrigiert: Failed-Reason hat Vorrang vor "noch nicht completed",
  // sonst sieht der User ewig "Backfill laeuft..." obwohl der Lookup schon
  // hard-gescheitert ist (z.B. Yahoo blockt Hetzner-IP).
  let statusLabel: string;
  let statusTone: "muted" | "warn" | "neg" | "pos" = "muted";
  if (polling || backfillRunning) {
    statusLabel = t("settings.benchmarks.status.pending");
  } else if (bm.initialBackfillFailedReason) {
    statusLabel = `${t("settings.benchmarks.status.failed")}: ${bm.initialBackfillFailedReason}`;
    statusTone = "neg";
  } else if (!bm.initialBackfillCompleted) {
    statusLabel = t("settings.benchmarks.status.pending");
    statusTone = "warn";
  } else {
    statusLabel = `${bm.quoteCount} ${t("settings.benchmarks.status.quotes")}`;
    statusTone = "pos";
  }

  const statusColor =
    statusTone === "pos"
      ? "var(--pos)"
      : statusTone === "neg"
        ? "var(--neg)"
        : statusTone === "warn"
          ? "var(--warn)"
          : "var(--muted)";

  return (
    <tr className="border-b" style={{ borderColor: "var(--border-soft)" }}>
      <td className="px-3 py-2.5">
        <input
          type="checkbox"
          checked={bm.isActiveForKpi}
          onChange={(e) => onToggleActive(e.target.checked)}
          disabled={!bm.initialBackfillCompleted}
          className="accent-[var(--accent)] cursor-pointer disabled:opacity-50"
        />
      </td>
      <td className="px-3 py-2.5">{bm.displayName}</td>
      <td className="px-3 py-2.5 mono">{bm.symbol}</td>
      <td className="px-3 py-2.5" style={{ color: "var(--muted)" }}>
        {bm.currency ?? "—"}
      </td>
      <td
        className="px-3 py-2.5 text-xs"
        style={{ color: statusColor }}
        title={statusLabel}
      >
        <span className="line-clamp-2">{statusLabel}</span>
      </td>
      <td className="px-3 py-2.5 text-right text-xs">
        <button
          type="button"
          className="mr-3 font-medium disabled:opacity-50"
          style={{ color: "var(--accent)" }}
          onClick={onBackfill}
          disabled={backfillRunning}
        >
          {backfillRunning
            ? `${t("settings.benchmarks.backfillBtn")}…`
            : t("settings.benchmarks.backfillBtn")}
        </button>
        <button
          type="button"
          className="font-medium"
          style={{ color: "var(--neg)" }}
          onClick={onDelete}
        >
          {t("settings.benchmarks.deleteBtn")}
        </button>
      </td>
    </tr>
  );
}

interface CreateModalProps {
  onCancel: () => void;
  onCreate: (input: { symbol: string; displayName: string; provider: "YAHOO" }) => void;
  submitting: boolean;
}

function CreateBenchmarkModal({ onCancel, onCreate, submitting }: CreateModalProps) {
  const { t } = useTranslation();
  const [symbol, setSymbol] = useState("");
  const [displayName, setDisplayName] = useState("");

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div
        className="rounded-xl p-6 space-y-4 w-96"
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
        }}
      >
        <h3 className="font-semibold text-base">{t("settings.benchmarks.modal.title")}</h3>
        <label className="block">
          <span
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: "var(--muted)" }}
          >
            {t("settings.benchmarks.modal.symbol")}
          </span>
          <input
            type="text"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 rounded-lg text-sm mono"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
            placeholder="URTH, ^GSPC, BTC-EUR"
          />
        </label>
        <label className="block">
          <span
            className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: "var(--muted)" }}
          >
            {t("settings.benchmarks.modal.displayName")}
          </span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 rounded-lg text-sm"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
        </label>
        <p className="text-xs" style={{ color: "var(--muted)" }}>
          {t("settings.benchmarks.modal.hint")}
        </p>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            onClick={onCancel}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={submitting || symbol.length === 0 || displayName.length === 0}
            className="px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ background: "var(--accent)" }}
            onClick={() => onCreate({ symbol, displayName, provider: "YAHOO" })}
          >
            {t("common.create")}
          </button>
        </div>
      </div>
    </div>
  );
}
